// src/redux/slices/testimonialAdminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { testimonialsService } from '../../services/testimonialsService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchTestimonialsAdmin = createAsyncThunk(
    'testimonialAdmin/fetchTestimonialsAdmin',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await testimonialsService.getTestimonials({
                ...params,
                limit: params.limit || 10,
                page: params.page || 1
            })
            return {
                testimonials: response.data.testimonials,
                pagination: response.data.pagination
            }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const createTestimonial = createAsyncThunk(
    'testimonialAdmin/createTestimonial',
    async (testimonialData, { rejectWithValue }) => {
        try {
            const response = await testimonialsService.createTestimonial(testimonialData)
            return response.data.testimonial
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateTestimonial = createAsyncThunk(
    'testimonialAdmin/updateTestimonial',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await testimonialsService.updateTestimonial(id, data)
            return response.data.testimonial
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const deleteTestimonial = createAsyncThunk(
    'testimonialAdmin/deleteTestimonial',
    async (id, { rejectWithValue }) => {
        try {
            await testimonialsService.deleteTestimonial(id)
            return id
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const testimonialAdminSlice = createSlice({
    name: 'testimonialAdmin',
    initialState: {
        testimonials: [],
        currentTestimonial: null,
        loading: false,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        },
        error: null
    },
    reducers: {
        clearCurrentTestimonial: (state) => {
            state.currentTestimonial = null
        },
        clearError: (state) => {
            state.error = null
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Testimonials Admin
            .addCase(fetchTestimonialsAdmin.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchTestimonialsAdmin.fulfilled, (state, action) => {
                state.loading = false
                state.testimonials = action.payload.testimonials
                state.pagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchTestimonialsAdmin.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Create Testimonial
            .addCase(createTestimonial.pending, (state) => {
                state.loading = true
            })
            .addCase(createTestimonial.fulfilled, (state, action) => {
                state.loading = false
                state.testimonials.unshift(action.payload)
                state.error = null
            })
            .addCase(createTestimonial.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Update Testimonial
            .addCase(updateTestimonial.pending, (state) => {
                state.loading = true
            })
            .addCase(updateTestimonial.fulfilled, (state, action) => {
                state.loading = false
                const index = state.testimonials.findIndex(testimonial => testimonial._id === action.payload._id)
                if (index !== -1) {
                    state.testimonials[index] = action.payload
                }
                state.error = null
            })
            .addCase(updateTestimonial.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Delete Testimonial
            .addCase(deleteTestimonial.fulfilled, (state, action) => {
                state.testimonials = state.testimonials.filter(testimonial => testimonial._id !== action.payload)
                state.error = null
            })
    }
})

export const { clearCurrentTestimonial, clearError, setPagination } = testimonialAdminSlice.actions
export default testimonialAdminSlice.reducer